import { useId } from 'react'
import { Icon } from '../ui'
import { clampPage, cleanSize, pageCount, pageWindow, PAGE_SIZES, rangeWords } from './flow'
import './flow.css'

// Pages for a long table (release 3.3, task F2 of the 7 Oct plan, rule 2.1).
//
// # WHY THIS EXISTS
//
// The vendor register drew every row it had, so a register of 742 vendors was
// one page several metres long, and the way back from a vendor landed at the
// top of it. A table shows 25 rows a page (50 or 100 on request), always with
// "1-25 of 142", so the reader knows how much there is without scrolling to
// find out.
//
// # WHAT IT PROMISES
//
//  - A nav landmark named by `label`; the page on screen carries
//    aria-current="page"; Previous and Next say so in words, not only arrows.
//  - Previous on the first page and Next on the last are off, not hidden, so
//    the row does not jump.
//  - A gap in the numbers is drawn as an ellipsis that is not a button.
//  - The screen owns the address: onPage and onSize are told the new value and
//    the screen REPLACES its address (rule B5: paging is not a history step).
//  - A size outside `sizes` is drawn as the default (cleanSize); the screen
//    slices its rows by cleanSize too, so the words and the rows agree.
//  - Under a ScrollPanel, pass the panel resetKey={page + ':' + size} so a new
//    page opens at its first row.

export function Pager({
  label = 'Pages',
  page,
  size,
  total,
  sizes = PAGE_SIZES,
  onPage,
  onSize,
}: {
  label?: string
  page: number
  size: number
  total: number
  sizes?: readonly number[]
  onPage: (page: number) => void
  onSize?: (size: number) => void
}) {
  const id = useId()
  const s = cleanSize(size, sizes)
  const pages = pageCount(total, s)
  const p = clampPage(page, total, s)
  return (
    <nav className="kit-pager" aria-label={label}>
      <span className="kit-pager-range" aria-live="polite">
        {rangeWords(p, s, total)}
      </span>
      {pages > 1 && (
        <span className="kit-pager-pages">
          <button type="button" className="kit-pager-step kit-pager-prev" disabled={p <= 1} onClick={() => onPage(p - 1)}>
            <Icon name="chevron" size={14} />
            <span className="vh">Previous page</span>
          </button>
          {pageWindow(p, pages).map((n, i) =>
            n === 'gap' ? (
              <span key={'gap' + i} className="kit-pager-gap" aria-hidden="true">
                ...
              </span>
            ) : (
              <button
                key={n}
                type="button"
                className={'kit-pager-n' + (n === p ? ' on' : '')}
                aria-current={n === p ? 'page' : undefined}
                aria-label={'Page ' + n}
                onClick={() => n !== p && onPage(n)}
              >
                {n}
              </button>
            ),
          )}
          <button type="button" className="kit-pager-step" disabled={p >= pages} onClick={() => onPage(p + 1)}>
            <span className="vh">Next page</span>
            <Icon name="chevron" size={14} />
          </button>
        </span>
      )}
      {onSize && total > Math.min(...sizes) && (
        <span className="kit-pager-size">
          <label htmlFor={id + 'size'}>Rows</label>
          <select id={id + 'size'} value={s} onChange={(e) => onSize(Number(e.target.value))}>
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </span>
      )}
    </nav>
  )
}
